import { css, useTheme } from "@emotion/react";
import { omit } from "lodash/fp";
import merge from "lodash/fp/merge";
import { Fragment, useContext, useState } from "react";

import {
  Icon,
  Heading,
  InlineList,
  TypographyBlock,
  useThemeProps,
  PageGrid,
  PageGridItem,
  Section,
  handleComponentsProp,
  generateUniqueId,
} from "../../wsui/base";

import PostsModuleArchiveLink from "./PostsModuleArchiveLink.jsx";
import postsModuleContext from "./postsModuleContext";
import PostsModuleItem from "./PostsModuleItem.jsx";

export default function PostsModuleInlineListLayout(props) {
  const theme = useTheme();
  props = useThemeProps({
    props,
    name: "MunicipioPostsModuleInlineListLayout",
  });
  let {
    title,
    titleIcon,
    titleIconProps = { size: 8 },
    description,
    archiveLink,
    archiveLinkProps,
    items,
    spacing = 3.75,
    components,
    headingVariant,
    itemVariant = "button",
    itemColor,
    align,
    itemAlign,
    stretchOnMaxWidth = 150,
    headingId,
    ...restProps
  } = omit(["gridMaxColumns"], props);
  const contentAlign =
    align === "left" || align === "center" || align === "right"
      ? align
      : undefined;
  const inlineListAlign =
    contentAlign === "left"
      ? "start"
      : contentAlign === "right"
        ? "end"
        : contentAlign === "center"
          ? "center"
          : undefined;
  const containerAlignItems =
    contentAlign === "left"
      ? "flex-start"
      : contentAlign === "right"
        ? "flex-end"
        : contentAlign === "center"
          ? "center"
          : "stretch";
  const archiveAlign =
    contentAlign === "right"
      ? "flex-end"
      : contentAlign === "center"
        ? "center"
        : "flex-start";
  const normalizedItemAlign =
    itemAlign === "left"
      ? "start"
      : itemAlign === "right"
        ? "end"
        : itemAlign;
  const effectiveItemAlign =
    (contentAlign === "right"
      ? "end"
      : contentAlign === "center"
        ? "center"
        : contentAlign === "left"
          ? "start"
          : normalizedItemAlign || "start");
  components = handleComponentsProp(components, { Item: PostsModuleItem });
  let { Item } = components;

  let parentOwnerState = useContext(postsModuleContext);
  let { headingPosition, visibleFields } = parentOwnerState;
  let ownerState = merge(parentOwnerState, {
    title,
    description,
    archiveLink,
    items,
    visibleFields,
    itemVariant,
    itemColor,
    headingPosition,
  });

  const [defaltHeadingId] = useState(
    () => `module-heading-${generateUniqueId()}`,
  );
  headingId = headingId || defaltHeadingId;

  const MaybeSection = title ? Section : Fragment;

  if (headingPosition === "left") {
    return (
      <postsModuleContext.Provider value={ownerState}>
        <PageGrid>
          <PageGridItem colspan={4}>
            {title && (
              <Heading
                variant={headingVariant}
                marginAfter
                id={headingId}
                css={css`
                  display: flex;
                  gap: 0.5em;
                  align-items: start;
                `}
              >
                {titleIcon && (
                  <Icon
                    name={titleIcon}
                    css={css`
                      align-self: center;
                      flex: none;
                    `}
                    {...titleIconProps}
                  />
                )}
                {title}
              </Heading>
            )}
            {description && (
              <TypographyBlock align="left">{description}</TypographyBlock>
            )}
            {archiveLink && (
              <PostsModuleArchiveLink
                align={archiveAlign}
                {...archiveLink}
                {...archiveLinkProps}
                css={css`
                  margin-top: ${theme.getLength(6)};
                `}
              />
            )}
          </PageGridItem>
          <PageGridItem colspan={8}>
            <MaybeSection>
              <InlineList
                spacing={spacing}
                stretchOnMaxWidth={stretchOnMaxWidth}
              >
                {items.map((item, index) => (
                  <Item
                    key={index}
                    item={item}
                    visibleFields={visibleFields}
                    variant={itemVariant}
                    width="100%"
                    align={effectiveItemAlign}
                  />
                ))}
              </InlineList>
            </MaybeSection>
          </PageGridItem>
        </PageGrid>
      </postsModuleContext.Provider>
    );
  } else {
    return (
      <postsModuleContext.Provider value={ownerState}>
        <div
          {...restProps}
          css={css`
            display: flex;
            flex-direction: column;
            align-items: ${containerAlignItems};
            text-align: ${contentAlign || "start"};
          `}
        >
          <div
            css={css`
              display: grid;
              grid-template-columns: 1fr max-content;
              align-items: start;
            `}
          >
            {title && (
              <Heading
                variant={headingVariant}
                marginAfter
                id={headingId}
                css={css`
                  display: flex;
                  gap: 0.5em;
                  align-items: start;
                `}
              >
                {titleIcon && (
                  <Icon
                    name={titleIcon}
                    css={css`
                      align-self: center;
                      flex: none;
                    `}
                    {...titleIconProps}
                  />
                )}
                {title}
              </Heading>
            )}
            {archiveLink && (
              <PostsModuleArchiveLink
                align={archiveAlign}
                {...archiveLink}
                {...archiveLinkProps}
              />
            )}
          </div>
          {description && (
            <TypographyBlock marginAfter align="left">{description}</TypographyBlock>
          )}
          <Section>
            <InlineList
              spacing={spacing}
              align={inlineListAlign}
              stretchOnMaxWidth={stretchOnMaxWidth}
            >
              {items.map((item, index) => (
                <Item
                  key={index}
                  item={item}
                  visibleFields={visibleFields}
                  variant={itemVariant}
                  width="100%"
                  align={effectiveItemAlign}
                />
              ))}
            </InlineList>
          </Section>
          {archiveLink && <PostsModuleArchiveLink align={archiveAlign} {...archiveLink} />}
        </div>
      </postsModuleContext.Provider>
    );
  }
}
